import React, { ReactNode, useState } from 'react';
import { TabsOption } from 'global';
import { useQuery } from '@apollo/client';
import { CircularProgress, Stack } from '@mui/material';
import { TabsContainer } from 'components/shared/TabsContainer/TabsContainer';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';

import { DealSheetRuleForm } from './rule';
import { RulesTableContainer } from './table';
import { getInitialValues } from './rule/lib';
import { PageViewType } from './table/types';
import { ConfigurationDetails } from './details';
import { dealSheetConfigurationApi } from '../api';
import { DealSheetConfigurationViewModel } from '../types';

export const DealSheet = () => {
  const [pageView, setPageView] = useState<{ type: PageViewType; id: string | null }>({
    type: 'tabs',
    id: null,
  });

  const {
    data,
    error,
    loading: isConfigLoading,
  } = useQuery<{ dealSheets: { result: { entity: DealSheetConfigurationViewModel } } }>(
    dealSheetConfigurationApi.getConfig,
    {
      skip: !pageView.id,
      notifyOnNetworkStatusChange: true,
      variables: { input: { id: pageView.id! } },
    }
  );

  const config = data?.dealSheets?.result?.entity ?? null;

  const tabs: TabsOption[] = [
    {
      value: 'rulesList',
      label: 'Business Rule',
      component: <RulesTableContainer onChangeView={(type, id) => setPageView({ type, id })} />,
    },
    {
      isVisible: false,
      value: 'configuration',
      label: 'Configuration',
      component: <>Configuration</>,
    },
  ];

  const render: Record<PageViewType, ReactNode> = {
    tabs: <TabsContainer tabs={tabs} />,
    create: (
      <DealSheetRuleForm
        id={pageView.id}
        isConfigLoading={isConfigLoading}
        initialValues={getInitialValues(null)}
        onCancel={() => setPageView({ type: 'tabs', id: null })}
      />
    ),
    update: (
      <RenderWithFallback
        error={error}
        isLoading={isConfigLoading}
        fallBack={
          <Stack alignItems="center" justifyContent="center" height="100%" width="100%">
            <CircularProgress />
          </Stack>
        }
      >
        <DealSheetRuleForm
          id={pageView.id}
          isConfigLoading={isConfigLoading}
          initialValues={getInitialValues(config)}
          onCancel={() => setPageView({ type: 'tabs', id: null })}
        />
      </RenderWithFallback>
    ),
    details: (
      <RenderWithFallback
        error={error}
        isLoading={isConfigLoading}
        fallBack={
          <Stack alignItems="center" justifyContent="center" height="100%" width="100%">
            <CircularProgress />
          </Stack>
        }
      >
        <ConfigurationDetails
          config={config}
          isConfigLoading={isConfigLoading}
          onChangeView={(type, id) => setPageView({ type, id })}
        />
      </RenderWithFallback>
    ),
  };

  return render[pageView.type];
};
